<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappCatalogAPI from 'dashboard/api/whatsappCatalog';
import ButtonV4 from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  inboxId: { type: Number, default: undefined },
});

const emit = defineEmits(['onSend', 'cancel', 'update:show']);

// Meta caps a product_list message at 30 items across all sections
const MAX_PRODUCTS = 30;

const { t } = useI18n();

const catalogs = ref([]);
const selectedCatalogId = ref('');
const products = ref([]);
const nextCursor = ref(null);
const selected = ref([]);
const searchQuery = ref('');
const caption = ref('');
const isLoading = ref(false);
const isLoadingMore = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const catalogOptions = computed(() =>
  catalogs.value.map(catalog => ({ value: catalog.id, label: catalog.name }))
);

const selectedIds = computed(() => selected.value.map(item => item.id));

const isSendDisabled = computed(() => selected.value.length === 0);

const alertError = (error, fallbackKey) => {
  useAlert(error.response?.data?.error || t(fallbackKey));
};

const fetchProducts = async ({ append = false } = {}) => {
  if (!selectedCatalogId.value) return;

  const loadingRef = append ? isLoadingMore : isLoading;
  loadingRef.value = true;
  try {
    const { data } = await WhatsappCatalogAPI.getProducts(props.inboxId, {
      catalogId: selectedCatalogId.value,
      q: searchQuery.value || undefined,
      after: append ? nextCursor.value : undefined,
    });
    products.value = append
      ? [...products.value, ...data.products]
      : data.products;
    nextCursor.value = data.next_cursor;
  } catch (error) {
    alertError(error, 'WHATSAPP_CATALOG.PRODUCTS_ERROR');
  } finally {
    loadingRef.value = false;
  }
};

const fetchCatalogs = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappCatalogAPI.getCatalogs(props.inboxId);
    catalogs.value = data.catalogs;
    selectedCatalogId.value = data.catalogs[0]?.id || '';
  } catch (error) {
    alertError(error, 'WHATSAPP_CATALOG.CATALOGS_ERROR');
  } finally {
    isLoading.value = false;
  }
};

const toggleProduct = product => {
  const index = selectedIds.value.indexOf(product.id);
  if (index > -1) {
    selected.value.splice(index, 1);
  } else if (selected.value.length < MAX_PRODUCTS) {
    selected.value.push(product);
  } else {
    useAlert(t('WHATSAPP_CATALOG.MAX_REACHED', { count: MAX_PRODUCTS }));
  }
};

const onSend = () => {
  if (isSendDisabled.value) return;

  emit('onSend', {
    message: caption.value,
    contentAttributes: {
      type: 'whatsapp_products',
      catalog_id: selectedCatalogId.value,
      items: selected.value.map(product => ({
        retailer_id: product.retailer_id,
        name: product.name,
        price: product.price,
        currency: product.currency,
        image_url: product.image_url,
      })),
    },
  });

  selected.value = [];
  caption.value = '';
};

watch(
  () => props.show,
  isVisible => {
    if (isVisible && !catalogs.value.length) fetchCatalogs();
  }
);

watch(selectedCatalogId, () => {
  products.value = [];
  nextCursor.value = null;
  selected.value = [];
  fetchProducts();
});

let searchTimer = null;
watch(searchQuery, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(fetchProducts, 400);
});
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="() => emit('cancel')">
    <woot-modal-header
      :header-title="$t('WHATSAPP_CATALOG.TITLE')"
      :header-content="$t('WHATSAPP_CATALOG.SUBTITLE')"
    />
    <div class="flex flex-col gap-4 p-8">
      <div class="flex items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('WHATSAPP_CATALOG.CATALOG') }}
          </label>
          <Select v-model="selectedCatalogId" :options="catalogOptions" />
        </div>
        <Input
          v-model="searchQuery"
          class="flex-1"
          :placeholder="$t('WHATSAPP_CATALOG.SEARCH_PLACEHOLDER')"
        />
      </div>

      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner />
      </div>
      <div
        v-else-if="!products.length"
        class="py-10 text-sm text-center text-n-slate-11"
      >
        {{ $t('WHATSAPP_CATALOG.EMPTY') }}
      </div>
      <div
        v-else
        class="grid grid-cols-2 gap-3 overflow-y-auto max-h-80 md:grid-cols-3"
      >
        <button
          v-for="product in products"
          :key="product.id"
          class="flex flex-col gap-1 p-2 text-left border rounded-lg"
          :class="
            selectedIds.includes(product.id)
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak'
          "
          @click="toggleProduct(product)"
        >
          <img
            v-if="product.image_url"
            :src="product.image_url"
            class="object-cover w-full rounded h-28"
            alt=""
          />
          <div
            v-else
            class="flex items-center justify-center w-full rounded h-28 bg-n-alpha-2"
          >
            <Icon icon="i-lucide-image" class="size-5 text-n-slate-11" />
          </div>
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ product.name }}
          </span>
          <span class="text-xs text-n-slate-11">{{ product.price }}</span>
          <span class="text-xs truncate text-n-slate-10">
            {{ product.retailer_id }}
          </span>
        </button>
      </div>

      <ButtonV4
        v-if="nextCursor"
        sm
        faded
        slate
        :is-loading="isLoadingMore"
        :label="$t('WHATSAPP_CATALOG.LOAD_MORE')"
        @click="fetchProducts({ append: true })"
      />

      <Input
        v-model="caption"
        :label="$t('WHATSAPP_CATALOG.CAPTION')"
        :placeholder="$t('WHATSAPP_CATALOG.CAPTION_PLACEHOLDER')"
      />

      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-11">
          {{ $t('WHATSAPP_CATALOG.SELECTED', { count: selected.length }) }}
        </span>
        <div class="flex gap-2">
          <ButtonV4
            faded
            slate
            :label="$t('WHATSAPP_CATALOG.CANCEL')"
            @click="emit('cancel')"
          />
          <ButtonV4
            :disabled="isSendDisabled"
            :label="$t('WHATSAPP_CATALOG.SEND')"
            @click="onSend"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
